import React from "react";
import { CheckCircle2, ShieldCheck, Clock, MapPin } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { PageHero, Reveal, SectionLabel } from "@/components/ui-kit";
import { BookingWidget } from "@/components/booking/BookingWidget";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CONFIG } from "@/config";

const POINTS = [
  "30-minute, no-obligation conversation",
  "Phone, WhatsApp, Google Meet or in person",
  "We review your goals before the call",
  "Clear next steps — no product pushing",
];

export default function BookPage() {
  const [params] = useSearchParams();
  usePageMeta({
    title: "Book a Free Consultation — Phone, WhatsApp, Video or In-Person",
    description: "Book a free 30-minute consultation with Auxieron. Pick a time that suits you — phone, WhatsApp, Google Meet or at our Bhubaneswar office — and get clear next steps for your money.",
    path: "/book",
  });

  return (
    <>
      <PageHero
        label="Book a consultation"
        title="Pick a time."
        highlight="We'll do the rest."
        lead="A free, no-pressure conversation about where you are and where you want to be. Choose how you'd like to meet, pick a slot, and you're set."
        testId="book-label"
      />

      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal className="min-w-0">
            <div id="booking" className="min-w-0 overflow-hidden rounded-3xl border border-navy/[0.08] bg-lightbg/50 p-6 sm:p-8">
              <BookingWidget defaultTopic={params.get("topic") || undefined} />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 lg:sticky lg:top-28">
              <div className="rounded-3xl bg-navy p-7 text-white">
                <SectionLabel dark testId="book-expect-label">What to expect</SectionLabel>
                <ul className="mt-5 space-y-3">
                  {POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-white/80"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" /> {p}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-navy/[0.08] bg-white p-7">
                <p className="flex items-center gap-2 text-sm font-semibold text-navy"><MapPin className="h-4 w-4 text-royal" /> Office</p>
                <p data-testid="book-office-address" className="mt-2 text-sm leading-relaxed text-ink/65">{CONFIG.address}</p>
                <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-navy"><Clock className="h-4 w-4 text-royal" /> Prefer to just call?</p>
                <a href={CONFIG.phoneHref} data-testid="book-phone" className="mt-1 block text-sm text-royal hover:text-navy">{CONFIG.phone}</a>
                <p className="mt-5 flex items-start gap-2 text-xs text-ink/50"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Your details are used only to arrange this appointment and are never shared.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
